// Admin Users (Super Admins) — list/create/edit/delete with Role + Permissions picker
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Shield, X, Pencil, Trash2, Check, KeyRound, Save } from "lucide-react";
import { API, getToken } from "../../lib/auth";
import { useConfirm } from "../../components/ConfirmDialog";

const cfg = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const cls = {
  card: "bg-white rounded-2xl border border-black/[0.06]",
  input: "w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm bg-white",
  label: "block text-[11px] font-bold uppercase tracking-wider text-black/55 mb-1.5",
  primary: "inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white text-sm font-bold hover:opacity-95 shadow-sm",
};

export default function AdminSuperAdmins() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState({ permissions: [], groups: {} });
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const [openRoles, setOpenRoles] = useState(false);
  const [openRoleEdit, setOpenRoleEdit] = useState(null);
  const [roleForm, setRoleForm] = useState({});
  const confirm = useConfirm();

  const load = async () => {
    const [u, r, p] = await Promise.all([
      axios.get(`${API}/admin/admin-users`, cfg()),
      axios.get(`${API}/admin/admin-roles`, cfg()),
      axios.get(`${API}/admin/admin-perms-catalog`, cfg()),
    ]);
    setRows(u.data); setRoles(r.data); setPerms(p.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ email: "", name: "", password: "", role_id: "", permissions: [], is_active: true }); setEdit(null); setOpen(true); };
  const openEdit = (u) => { setForm({ ...u, password: "" }); setEdit(u); setOpen(true); };

  const togglePerm = (p) => setForm((f) => ({ ...f, permissions: (f.permissions || []).includes(p) ? f.permissions.filter((x) => x !== p) : [...(f.permissions || []), p] }));

  const submit = async () => {
    if (!form.email || !form.name) return toast.error("Email + name required");
    if (!edit && (!form.password || form.password.length < 8)) return toast.error("Password 8+ chars required");
    if (!(await confirm({ title: edit ? "Save admin user?" : "Create admin user?", message: form.email }))) return;
    try {
      if (edit) await axios.patch(`${API}/admin/admin-users/${edit.id}`, form, cfg());
      else await axios.post(`${API}/admin/admin-users`, form, cfg());
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (u) => {
    if (!(await confirm({ title: "Delete admin user?", message: `Remove ${u.email}?`, tone: "danger", yesLabel: "Delete" }))) return;
    try {
      await axios.delete(`${API}/admin/admin-users/${u.id}`, cfg());
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Roles modal
  const openCreateRole = () => { setRoleForm({ name: "", description: "", permissions: [] }); setOpenRoleEdit(null); setOpenRoles(true); };
  const openEditRole = (r) => { setRoleForm({ ...r }); setOpenRoleEdit(r); setOpenRoles(true); };
  const togglePermInRole = (p) => setRoleForm((f) => ({ ...f, permissions: (f.permissions || []).includes(p) ? f.permissions.filter((x) => x !== p) : [...(f.permissions || []), p] }));
  const submitRole = async () => {
    if (!roleForm.name) return toast.error("Name required");
    if (!(await confirm({ title: openRoleEdit ? "Save role?" : "Create role?", message: roleForm.name }))) return;
    try {
      if (openRoleEdit) await axios.patch(`${API}/admin/admin-roles/${openRoleEdit.id}`, roleForm, cfg());
      else await axios.post(`${API}/admin/admin-roles`, roleForm, cfg());
      toast.success("Saved"); setOpenRoles(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delRole = async (r) => {
    if (!(await confirm({ title: "Delete role?", tone: "danger", yesLabel: "Delete" }))) return;
    await axios.delete(`${API}/admin/admin-roles/${r.id}`, cfg());
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-superadmins-page" className="p-6 lg:p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Admin Users</h1>
          <p className="text-xs text-black/55 mt-1">Master Admin / Super Admins & Roles</p>
        </div>
        <div className="flex gap-2">
          <button onClick={openCreateRole} data-testid="admin-roles-new" className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-bold hover:bg-black/[0.02]"><Shield size={14} /> Manage Roles</button>
          <button onClick={openCreate} data-testid="admin-user-new" className={cls.primary}><Plus size={14} /> New Admin</button>
        </div>
      </div>

      {/* Roles strip */}
      {roles.length > 0 && (
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3 mb-5">
          {roles.map((r) => (
            <div key={r.id} className={`${cls.card} p-4`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold truncate">{r.name}</div>
                  <div className="text-[11px] text-black/55 truncate">{r.description || "—"}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEditRole(r)} className="p-1.5 rounded hover:bg-black/5"><Pencil size={12} /></button>
                  <button onClick={() => delRole(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={12} /></button>
                </div>
              </div>
              <div className="text-[10px] text-black/55 mt-2">{r.permissions?.length || 0} permission(s)</div>
            </div>
          ))}
        </div>
      )}

      <div className={`${cls.card}`}>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr><th className="text-left p-3.5">Name</th><th className="text-left p-3.5">Email</th><th className="text-left p-3.5">Role</th><th className="text-left p-3.5">Permissions</th><th className="text-left p-3.5">Status</th><th className="text-right p-3.5">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((u) => (
              <tr key={u.id} className="hover:bg-[#FAFBFB]">
                <td className="p-3.5"><div className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-[#E8F5E9] text-[#1FA868] font-extrabold text-[10px] flex items-center justify-center">{(u.name || "?").slice(0, 2).toUpperCase()}</div><div className="font-semibold">{u.name}</div></div></td>
                <td className="p-3.5">{u.email}</td>
                <td className="p-3.5">{u.is_master ? <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-violet-100 text-violet-700">Master</span> : (u.role_name || "Admin")}</td>
                <td className="p-3.5 text-[11px] text-black/55">{u.is_master ? "All" : (u.permissions || []).slice(0, 3).join(", ") + ((u.permissions || []).length > 3 ? `, +${(u.permissions || []).length - 3}` : "") || "—"}</td>
                <td className="p-3.5"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${u.is_active ? "bg-emerald-100 text-emerald-700" : "bg-black/5 text-black/55"}`}>{u.is_active ? "Active" : "Inactive"}</span></td>
                <td className="p-3.5 text-right">
                  {!u.is_master && <button onClick={() => openEdit(u)} className="p-1.5 rounded hover:bg-black/5 mr-1"><Pencil size={13} /></button>}
                  {!u.is_master && <button onClick={() => del(u)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button>}
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="p-10 text-center text-black/45">No admin users</td></tr>}
          </tbody>
        </table>
      </div>

      {/* User modal */}
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-black/5">
              <h3 className="font-bold">{edit ? "Edit Admin User" : "New Admin User"}</h3>
              <button onClick={() => setOpen(false)}><X size={15} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3 overflow-y-auto">
              <div><label className={cls.label}>Name</label><input className={cls.input} value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="au-name" /></div>
              <div><label className={cls.label}>Email</label><input className={cls.input} value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="au-email" /></div>
              <div className="col-span-2"><label className={cls.label}>{edit ? "New Password (leave blank to keep)" : "Password (8+ chars)"}</label><input type="password" className={cls.input} value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="au-password" /></div>
              <div><label className={cls.label}>Role</label>
                <select className={cls.input} value={form.role_id || ""} onChange={(e) => setForm({ ...form, role_id: e.target.value })} data-testid="au-role">
                  <option value="">— No role (use custom permissions) —</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div><label className={cls.label}>Active</label>
                <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="au-active" /> <span className="text-sm">Account is enabled</span></label>
              </div>
              <div className="col-span-2">
                <label className={cls.label}>Custom Permissions {form.role_id && <span className="text-amber-700 font-normal normal-case">· combined with role permissions</span>}</label>
                <div className="grid md:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto p-3 rounded-lg bg-[#FAFBFB] border border-black/5">
                  {Object.entries(perms.groups || {}).map(([g, items]) => (
                    <div key={g} className="col-span-2 mt-1">
                      <div className="text-[10px] font-extrabold uppercase tracking-wider text-black/45 mb-1">{g}</div>
                      <div className="grid grid-cols-2 gap-1">
                        {items.map((p) => (
                          <label key={p} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-white rounded px-2 py-1">
                            <input type="checkbox" checked={(form.permissions || []).includes(p)} onChange={() => togglePerm(p)} data-testid={`au-perm-${p}`} />
                            <span>{p}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <button onClick={submit} className={cls.primary} data-testid="au-save"><Save size={13} /> Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Role modal */}
      {openRoles && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-xl shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-black/5">
              <h3 className="font-bold">{openRoleEdit ? "Edit Role" : "New Role"}</h3>
              <button onClick={() => setOpenRoles(false)}><X size={15} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div><label className={cls.label}>Name</label><input className={cls.input} value={roleForm.name || ""} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} data-testid="role-f-name" /></div>
              <div><label className={cls.label}>Description</label><input className={cls.input} value={roleForm.description || ""} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} /></div>
              <div>
                <label className={cls.label}>Permissions</label>
                <div className="grid grid-cols-2 gap-1 max-h-56 overflow-y-auto p-3 rounded-lg bg-[#FAFBFB] border border-black/5">
                  {(perms.permissions || []).map((p) => (
                    <label key={p} className="flex items-center gap-1.5 text-xs">
                      <input type="checkbox" checked={(roleForm.permissions || []).includes(p)} onChange={() => togglePermInRole(p)} data-testid={`role-perm-${p}`} />
                      <span>{p}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpenRoles(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <button onClick={submitRole} className={cls.primary} data-testid="role-f-save"><Save size={13} /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
